/**
 * TranscriptViewer Component (shared between admin + portal)
 * Clickable timestamps for video seek (FR-042)
 */

import { useState } from 'react';
import { Search, Clock } from 'lucide-react';

interface Props {
  transcript: string;
  onSeek?: (seconds: number) => void;
}

/**
 * Parse VTT/SRT-style transcript into segments
 * Handles formats like:
 *   00:01:23 Speaker: Text
 *   [00:01:23] Text
 *   1:23 Text
 */
function parseTranscript(raw: string): { timestamp: number; text: string; label: string }[] {
  const lines = raw.split('\n');
  const segments: { timestamp: number; text: string; label: string }[] = [];
  const timeRegex = /\[?(\d{1,2}:?\d{2}(?::\d{2})?(?:\.\d+)?)\]?\s*(.*)/;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      continue;
    }

    const match = trimmed.match(timeRegex);
    if (match) {
      const timeStr = match[1];
      const text = match[2] || '';
      const seconds = parseTimestamp(timeStr);
      segments.push({
        timestamp: seconds,
        text: text.trim(),
        label: formatTimestamp(seconds),
      });
    } else if (segments.length > 0) {
      // Continuation line — append to last segment
      segments[segments.length - 1].text += ` ${trimmed}`;
    } else {
      // No timestamp prefix — treat as plain text with index-based timestamp
      segments.push({
        timestamp: segments.length * 30,
        text: trimmed,
        label: formatTimestamp(segments.length * 30),
      });
    }
  }

  return segments;
}

function parseTimestamp(ts: string): number {
  const parts = ts.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return parts[0] || 0;
}

function formatTimestamp(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function TranscriptViewer({ transcript, onSeek }: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const segments = parseTranscript(transcript);

  const filtered = searchQuery.trim()
    ? segments.filter((s) => s.text.toLowerCase().includes(searchQuery.toLowerCase()))
    : segments;

  return (
    <div className="space-y-3">
      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search transcript..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="claude-input pl-10 text-sm w-full"
        />
      </div>

      {/* Segments */}
      <div className="max-h-[50vh] overflow-y-auto space-y-1 pr-2">
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {searchQuery ? 'No matching segments found.' : 'No transcript content.'}
          </p>
        ) : (
          filtered.map((segment, idx) => (
            <div
              key={idx}
              className="flex gap-3 p-2 rounded-md hover:bg-muted/40 transition-colors group"
            >
              <button
                onClick={() => onSeek?.(segment.timestamp)}
                className="shrink-0 flex items-center gap-1 text-xs font-mono text-primary hover:underline"
                title="Jump to this timestamp"
              >
                <Clock className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                {segment.label}
              </button>
              <p className="text-sm leading-relaxed">
                {searchQuery ? highlightMatch(segment.text, searchQuery) : segment.text}
              </p>
            </div>
          ))
        )}
      </div>

      {searchQuery && filtered.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {filtered.length} of {segments.length} segments match
        </p>
      )}
    </div>
  );
}

function highlightMatch(text: string, query: string): React.ReactNode {
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) {
    return text;
  }

  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 dark:bg-yellow-800/50 rounded px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}
